import { useState } from 'react'
import { Plus } from 'lucide-react'
import { Button } from '@ludus/ui'
import { useTranslation } from 'react-i18next'
import type { StatusId } from '@ludus/core'
import { usePipelineMeta } from '../../data/usePipelinesSource.ts'
import { pipelineAccent } from '@/shared/utils/pipelineAccent.ts'
import { NewLeadDialog } from '../Dialogs/NewLeadDialog.tsx'
import { Breadcrumb } from '@/shared/breadcrumb/Breadcrumb.tsx'
import { usePipelineBackfill } from '../../Sync/usePipelineBackfill.ts'
import { describeCoverage } from '../../utils/coverage.ts'

interface PipelineHeaderProps {
  stageCount: number
  totalLeads: number
  totalValue: number
  e2eRate: number
  /** Stage to default the New-lead dialog to. Falls back to the first stage when null. */
  defaultStageId: StatusId | null
}

const compactFormatter = new Intl.NumberFormat('en-EU', {
  style: 'currency',
  currency: 'EUR',
  notation: 'compact',
  maximumFractionDigits: 1,
})

export function PipelineHeader({
  stageCount,
  totalLeads,
  totalValue,
  e2eRate,
  defaultStageId,
}: PipelineHeaderProps) {
  const { t } = useTranslation()
  const pipeline = usePipelineMeta()
  const accent = pipelineAccent(pipeline)
  const initial = pipeline.name.trim().charAt(0).toUpperCase() || '•'
  const [newLeadOpen, setNewLeadOpen] = useState(false)
  const { state: backfill } = usePipelineBackfill(pipeline.id)
  const coverage = describeCoverage(backfill)

  return (
    <header className="flex flex-col gap-2 px-1 pb-1">
      {/* Wayfinding sits above the panel (mirrors the lead-detail page, where the
          breadcrumb is above the hero) — separating navigation from identity. */}
      <Breadcrumb
        items={[{ label: t('nav.pipelines'), to: '/pipelines' }, { label: pipeline.name }]}
      />

      {/* Identity panel — shares the board's elevated gradient so header + board
          read as one surface, with the per-pipeline accent glow for identity
          (continuous wayfinding colour list → kanban → detail). */}
      <div className="relative overflow-hidden rounded-xl border border-border bg-gradient-panel-elevated px-4 py-3">
        {/* Accent corner glow — top-left. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-70"
          style={{
            background: `radial-gradient(120% 90% at 0% 0%, ${accent.glow} 0%, transparent 60%)`,
          }}
        />
        {/* Accent inset ring. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-xl"
          style={{ boxShadow: `inset 0 0 0 1px ${accent.ring}` }}
        />

        <div className="relative flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <span
              aria-hidden="true"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-base font-semibold"
              style={{
                backgroundColor: accent.tile,
                color: accent.hex,
                boxShadow: `inset 0 0 0 1px ${accent.ring}`,
              }}
            >
              {initial}
            </span>
            <div className="flex min-w-0 flex-col gap-0.5">
              <h1 className="min-w-0 truncate text-base font-semibold tracking-tight text-foreground sm:text-lg">
                {pipeline.name}
              </h1>
              <p className="text-xs text-muted">
                {t('pages.pipelines.summary', {
                  defaultValue:
                    '{{stages}} stages · {{deals}} deals · {{value}} open · {{rate}}% e2e',
                  stages: stageCount,
                  deals: totalLeads,
                  value: compactFormatter.format(totalValue),
                  rate: e2eRate.toFixed(1),
                })}
              </p>
              {coverage && (
                <p className="text-[11px] text-muted">
                  {t('pages.pipelines.backfill.covered', {
                    defaultValue: 'History covered: {{from}} – {{to}}',
                    from: coverage.from,
                    to: coverage.to,
                  })}
                </p>
              )}
            </div>
          </div>

          {stageCount > 0 && (
            <Button variant="primary" size="sm" onClick={() => setNewLeadOpen(true)}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              <span>{t('pages.pipelines.actions.newLead', { defaultValue: 'New deal' })}</span>
            </Button>
          )}
        </div>
      </div>

      <NewLeadDialog
        open={newLeadOpen}
        onOpenChange={setNewLeadOpen}
        pipelineId={pipeline.id}
        defaultStageId={defaultStageId}
      />
    </header>
  )
}
